/**
 * The output panel: one tab per view, as VS Code's panel shows Output and
 * Terminal. A view is either a text channel, such as the build's output, or
 * an element the page gives it, such as the serial monitor. In a channel,
 * compiler locations ("/project/x/main/app.c:12:5: error: ...") are links
 * that open the file at that line.
 */

interface View {
    name: string;
    tab: HTMLButtonElement;
    element: HTMLElement;
    /** For a channel: where its text goes, the length drawn, and text not yet drawn. */
    pre?: HTMLPreElement;
    chars: number;
    pending: string;
}

const LOCATION = /(\/[^\s:'"()]+):(\d+)(?::(\d+))?/g;
/** Keep a channel's DOM bounded; the oldest lines go first. */
const MAX_CHARS = 2_000_000;

export class Panel {
    readonly element: HTMLElement;
    readonly #tabs: HTMLElement;
    readonly #body: HTMLElement;
    readonly #clear: HTMLButtonElement;
    readonly #views = new Map<string, View>();
    #active: View | undefined;
    #frame = 0;
    /** The view `select` asked for before it was added, shown once it is. */
    #wanted: string | undefined;
    readonly #selected = new Set<(name: string) => void>();
    readonly #open: (path: string, line: number, column: number) => void;
    readonly #shown: () => void;

    /** `shown` is called when `show` selects a view, so the page can reveal the panel. */
    constructor(open: (path: string, line: number, column: number) => void, shown: () => void = () => {}) {
        this.#open = open;
        this.#shown = shown;
        this.element = document.createElement("div");
        this.element.className = "wb-panel";
        const header = document.createElement("div");
        header.className = "wb-panel-header";
        this.#tabs = document.createElement("div");
        this.#tabs.className = "wb-panel-tabs";
        this.#tabs.setAttribute("role", "tablist");
        const actions = document.createElement("div");
        actions.className = "wb-panel-actions";
        this.#clear = document.createElement("button");
        this.#clear.type = "button";
        this.#clear.className = "codicon codicon-clear-all";
        this.#clear.title = "Clear Output";
        this.#clear.addEventListener("click", () => this.clear());
        actions.append(this.#clear);
        header.append(this.#tabs, actions);
        this.#body = document.createElement("div");
        this.#body.className = "wb-panel-body";
        this.#body.addEventListener("click", (event) => {
            const link = (event.target as HTMLElement).closest<HTMLElement>("a[data-path]");
            if (link === null) return;
            event.preventDefault();
            this.#open(link.dataset["path"]!, Number(link.dataset["line"]), Number(link.dataset["column"] ?? 1));
        });
        this.element.append(header, this.#body);
    }

    /** Add a view the page draws itself; the first view added is shown first. */
    addView(name: string, element: HTMLElement): void {
        element.classList.add("wb-panel-view");
        this.#add({ name, tab: this.#tab(name), element, chars: 0, pending: "" });
    }

    show(name: string): void {
        this.#wanted = undefined;
        this.#select(this.#channel(name));
        this.#shown();
    }

    /**
     * Show a view without revealing the panel. A view not added yet is shown
     * when it is, unless another is chosen first: a channel appears with its
     * first text.
     */
    select(name: string): void {
        const view = this.#views.get(name);
        this.#wanted = view === undefined ? name : undefined;
        if (view !== undefined) this.#select(view);
    }

    /** Called with the view's name whenever another view is shown. */
    onSelect(listener: (name: string) => void): void {
        this.#selected.add(listener);
    }

    append(name: string, text: string): void {
        const channel = this.#channel(name);
        channel.pending += text;
        this.#frame ||= requestAnimationFrame(() => this.#flush());
    }

    clear(): void {
        if (this.#active?.pre === undefined) return;
        this.#active.pre.replaceChildren();
        this.#active.chars = 0;
        this.#active.pending = "";
    }

    #flush(): void {
        this.#frame = 0;
        for (const view of this.#views.values()) {
            const pre = view.pre;
            if (pre === undefined || view.pending === "") continue;
            const scroller = view.element;
            const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4;
            pre.append(linkify(view.pending));
            view.chars += view.pending.length;
            view.pending = "";
            while (view.chars > MAX_CHARS && pre.firstChild) {
                view.chars -= pre.firstChild.textContent?.length ?? 0;
                pre.firstChild.remove();
            }
            if (atBottom) scroller.scrollTop = scroller.scrollHeight;
        }
    }

    /** The channel called `name`, made on first use. */
    #channel(name: string): View {
        const existing = this.#views.get(name);
        if (existing !== undefined) return existing;
        const element = document.createElement("div");
        element.className = "wb-panel-view wb-panel-channel";
        const pre = document.createElement("pre");
        element.append(pre);
        const view: View = { name, tab: this.#tab(name), element, pre, chars: 0, pending: "" };
        this.#add(view);
        return view;
    }

    #add(view: View): void {
        this.#views.set(view.name, view);
        view.tab.addEventListener("click", () => {
            this.#wanted = undefined;
            this.#select(view);
        });
        view.element.hidden = true;
        view.element.setAttribute("role", "tabpanel");
        view.element.setAttribute("aria-label", view.name);
        this.#tabs.append(view.tab);
        this.#body.append(view.element);
        if (this.#wanted === view.name) this.#wanted = undefined;
        else if (this.#active !== undefined) return;
        this.#select(view);
    }

    #tab(name: string): HTMLButtonElement {
        const tab = document.createElement("button");
        tab.type = "button";
        tab.className = "wb-panel-tab";
        tab.setAttribute("role", "tab");
        tab.textContent = name;
        return tab;
    }

    #select(view: View): void {
        const changed = view !== this.#active;
        for (const other of this.#views.values()) {
            other.element.hidden = other !== view;
            other.tab.classList.toggle("active", other === view);
            other.tab.setAttribute("aria-selected", String(other === view));
        }
        this.#active = view;
        this.#clear.hidden = view.pre === undefined;
        if (view.pre !== undefined) view.element.scrollTop = view.element.scrollHeight;
        if (changed) for (const listener of this.#selected) listener(view.name);
    }
}

function linkify(text: string): DocumentFragment {
    const fragment = document.createDocumentFragment();
    let last = 0;
    for (const m of text.matchAll(LOCATION)) {
        fragment.append(text.slice(last, m.index));
        const link = document.createElement("a");
        link.href = "#";
        link.textContent = m[0];
        link.dataset["path"] = m[1]!;
        link.dataset["line"] = m[2]!;
        if (m[3] !== undefined) link.dataset["column"] = m[3];
        fragment.append(link);
        last = m.index + m[0].length;
    }
    fragment.append(text.slice(last));
    return fragment;
}
